import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@monigo/ui';
import { AuthLayout } from '../../components/Layout/AuthLayout.js';

export function EmailVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(45);
  const [canResend, setCanResend] = useState(false);

  const email = (location.state as { email?: string } | null)?.email || '';

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError(t('auth.errors.invalidCode'));
      return;
    }

    setIsLoading(true);
    // TODO: Call verification endpoint
    // For now, just navigate on success
    setTimeout(() => {
      setIsLoading(false);
      navigate('/app');
    }, 1000);
  };

  const handleResend = async () => {
    setCanResend(false);
    setCountdown(45);
    // TODO: Call resend code endpoint
  };

  return (
    <AuthLayout
      title={t('auth.emailVerificationTitle')}
      subtitle={t('auth.emailVerificationSubtitle', { email })}
      backTo="/signup"
    >
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[1.25rem] bg-text text-3xl text-white shadow-[0_16px_35px_hsl(var(--color-text)/0.18)]">
          ✉
        </div>
        <div>
          <h1 className="text-4xl font-display font-extrabold text-text">
            {t('auth.emailVerificationTitle')}
          </h1>
          <p className="mx-auto mt-3 max-w-sm text-base leading-7 text-text-muted">
            {t('auth.emailVerificationSubtitle', { email })}
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="flex justify-center gap-2">
            {Array.from({ length: 6 }, (_, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code[index] ?? ''}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 1);
                  const chars = code.split('');
                  chars[index] = next;
                  setCode(chars.join('').slice(0, 6));
                  setError(null);
                }}
                className="h-14 w-12 rounded-[1rem] border border-border bg-surface text-center text-2xl font-extrabold text-text shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            ))}
          </div>

          {error ? (
            <p className="rounded-2xl bg-danger/10 p-3 text-sm text-danger">{error}</p>
          ) : null}

          <div className="space-y-2 text-sm">
            <p className="text-text-muted">
              {canResend ? (
                <button onClick={handleResend} type="button" className="font-semibold text-primary">
                  {t('auth.emailVerificationResend', { countdown: '0' })}
                </button>
              ) : (
                t('auth.emailVerificationResend', { countdown })
              )}
            </p>
            <button
              onClick={() => navigate('/login')}
              type="button"
              className="font-semibold text-text-muted underline"
            >
              {t('auth.emailVerificationChange')}
            </button>
          </div>

          <Button type="submit" isLoading={isLoading} className="w-full bg-text hover:bg-text/92">
            {t('auth.emailVerificationCta')}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
